import React, { useState } from 'react';
import { Download, ArrowUpDown, HelpCircle, ChevronDown, ChevronUp, FileSpreadsheet } from 'lucide-react';
import { SimulationResult, YearlySimulationResult } from './types';
import { SimulationCharts } from './SimulationCharts';

interface SimulationResultsPanelProps {
  result: SimulationResult | null;
  isLoading: boolean;
  onSave?: () => void;
  isSaved?: boolean;
}

export function SimulationResultsPanel({
  result,
  isLoading,
  onSave,
  isSaved = false
}: SimulationResultsPanelProps) {
  const [activeTab, setActiveTab] = useState<'summary' | 'yearly' | 'distribution'>('summary');
  const [showFullTable, setShowFullTable] = useState(false);

  if (isLoading) {
    return (
      <div className="bg-theme-secondary rounded-lg shadow-lg border border-theme-tertiary p-6">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <h3 className="text-lg font-medium text-theme-primary">
            Running Simulation...
          </h3>
          <p className="text-theme-secondary mt-2">
            This may take a few moments
          </p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="bg-theme-secondary rounded-lg shadow-lg border border-theme-tertiary p-6">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <HelpCircle size={48} className="text-theme-muted mb-4" />
          <h3 className="text-lg font-medium text-theme-primary">
            No Simulation Results Yet
          </h3>
          <p className="text-theme-secondary mt-2 max-w-md">
            Adjust your parameters and click "Run Simulation" to see investment
            projections
          </p>
        </div>
      </div>
    );
  }

  const {
    summary,
    yearlyResults,
    distribution,
    parameters
  } = result;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const downloadCSV = () => {
    // Header row
    let csv = 'Year,Portfolio Value,Share Price,Shares,Yearly Return,Event Impact,Event Description\n';
    // Data rows
    yearlyResults.forEach(yr => {
      csv += `${yr.year},${yr.portfolioValue},${yr.sharePrice},${yr.numberOfShares},${yr.yearlyReturn},${yr.eventImpact || ''},${yr.eventDescription || ''}\n`;
    });
    // Create download link
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'simulation_results.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="bg-theme-secondary rounded-lg shadow-lg border border-theme-tertiary">
      {/* Tabs */}
      <div className="flex border-b border-theme-tertiary">
        <button onClick={() => setActiveTab('summary')} className={`px-4 py-3 text-sm font-medium ${activeTab === 'summary' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-theme-secondary hover:text-theme-primary'}`}>
          Summary
        </button>
        <button onClick={() => setActiveTab('yearly')} className={`px-4 py-3 text-sm font-medium ${activeTab === 'yearly' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-theme-secondary hover:text-theme-primary'}`}>
          Yearly Breakdown
        </button>
        <button onClick={() => setActiveTab('distribution')} className={`px-4 py-3 text-sm font-medium ${activeTab === 'distribution' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-theme-secondary hover:text-theme-primary'}`}>
          Distribution
        </button>
        <div className="ml-auto flex items-center pr-4">
          <button onClick={downloadCSV} className="flex items-center text-sm text-theme-secondary hover:text-theme-primary px-3 py-2">
            <FileSpreadsheet size={16} className="mr-2" />
            Download CSV
          </button>
        </div>
      </div>
      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'summary' && <div>
          {/* Charts */}
          <SimulationCharts yearlyResults={yearlyResults} distribution={distribution} activeView="summary" />
          {/* Summary Stats */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-theme-tertiary rounded-lg p-4">
              <div className="text-sm text-theme-muted mb-1">
                Final Average Value
              </div>
              <div className="text-2xl font-bold text-theme-primary">
                {formatCurrency(summary.finalAverageValue)}
              </div>
              <div className="text-xs text-theme-secondary mt-1">
                Initial: {formatCurrency(parameters.initialInvestment)}
              </div>
            </div>
            <div className="bg-theme-tertiary rounded-lg p-4">
              <div className="text-sm text-theme-muted mb-1">
                Total Return
              </div>
              <div className="text-2xl font-bold text-green-500">
                {formatPercent(summary.totalReturn)}
              </div>
              <div className="text-xs text-theme-secondary mt-1">
                Annualized: {formatPercent(summary.annualizedReturn)}
              </div>
            </div>
            <div className="bg-theme-tertiary rounded-lg p-4">
              <div className="text-sm text-theme-muted mb-1">Value Range</div>
              <div className="text-lg font-medium text-theme-primary">
                {formatCurrency(summary.minValue)} -{' '}
                {formatCurrency(summary.maxValue)}
              </div>
              <div className="text-xs text-theme-secondary mt-1">
                Std Dev: {formatCurrency(summary.standardDeviation)}
              </div>
            </div>

            {/* SAVE ACTION CARD */}
            <div
              onClick={onSave}
              className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg p-4 cursor-pointer hover:shadow-lg hover:to-indigo-600 transition-all group relative overflow-hidden flex flex-col justify-between"
            >
              <div className="absolute top-0 right-0 p-2 opacity-20 group-hover:opacity-30 transition-opacity">
                <FileSpreadsheet size={48} />
              </div>
              <div className="text-sm text-blue-100 mb-1 font-medium">
                {isSaved ? 'Saved to Profile' : 'Save Analysis'}
              </div>
              <div className="text-xl font-bold text-white flex items-center gap-2">
                {isSaved ? 'Analysis Saved' : 'Save Results'}
              </div>
              <div className="text-xs text-blue-200 mt-1 flex items-center gap-1">
                {isSaved ? 'View in Dashboard' : 'Track this scenario over time'}
                <ArrowUpDown size={12} className="rotate-90" />
              </div>
            </div>

          </div>
        </div>}
        {activeTab === 'yearly' && <div>
          <SimulationCharts yearlyResults={yearlyResults} distribution={distribution} activeView="yearly" />
          <div className="mt-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium text-theme-primary">
                Yearly Breakdown
              </h3>
              <button onClick={() => setShowFullTable(!showFullTable)} className="flex items-center text-xs text-theme-secondary hover:text-theme-primary">
                {showFullTable ? <>
                  <ChevronUp size={14} className="mr-1" />
                  Show Less
                </> : <>
                  <ChevronDown size={14} className="mr-1" />
                  Show All Years
                </>}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-theme-tertiary">
                    <th className="py-2 px-4 text-xs font-medium text-theme-secondary">
                      Year
                    </th>
                    <th className="py-2 px-4 text-xs font-medium text-theme-secondary">
                      Portfolio Value
                    </th>
                    <th className="py-2 px-4 text-xs font-medium text-theme-secondary">
                      Share Price
                    </th>
                    <th className="py-2 px-4 text-xs font-medium text-theme-secondary">
                      Shares
                    </th>
                    <th className="py-2 px-4 text-xs font-medium text-theme-secondary">
                      Yearly Return
                    </th>
                    <th className="py-2 px-4 text-xs font-medium text-theme-secondary">
                      Event Impact
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(showFullTable ? yearlyResults : yearlyResults.slice(0, 5)).map(yr => <tr key={yr.year} className="border-b border-theme-tertiary hover:bg-theme-tertiary/70">
                    <td className="py-3 px-4 font-medium text-theme-primary">
                      {yr.year}
                    </td>
                    <td className="py-3 px-4 text-theme-primary">
                      {formatCurrency(yr.portfolioValue)}
                    </td>
                    <td className="py-3 px-4 text-theme-primary">
                      ${yr.sharePrice.toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-theme-primary">
                      {Math.round(yr.numberOfShares)}
                    </td>
                    <td className={`py-3 px-4 ${yr.yearlyReturn >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {formatPercent(yr.yearlyReturn)}
                    </td>
                    <td className="py-3 px-4">
                      {yr.eventImpact ? <span className={yr.eventImpact >= 0 ? 'text-green-500' : 'text-red-500'}>
                        {formatPercent(yr.eventImpact)} -{' '}
                        {yr.eventDescription}
                      </span> : <span className="text-theme-muted">None</span>}
                    </td>
                  </tr>)}
                </tbody>
              </table>
            </div>
            {!showFullTable && yearlyResults.length > 5 && <div className="mt-2 text-center">
              <button onClick={() => setShowFullTable(true)} className="text-xs text-blue-500 hover:text-blue-400">
                Show all {yearlyResults.length} years
              </button>
            </div>}
          </div>
        </div>}
        {activeTab === 'distribution' && <div>
          <SimulationCharts yearlyResults={yearlyResults} distribution={distribution} activeView="distribution" />
          <div className="mt-6">
            <h3 className="text-sm font-medium text-theme-primary mb-3">
              Outcome Probabilities
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-theme-tertiary rounded-lg p-4">
                <div className="text-sm text-theme-muted mb-1">
                  90th Percentile
                </div>
                <div className="text-xl font-bold text-theme-primary">
                  {formatCurrency(distribution.percentiles['90'])}
                </div>
                <div className="text-xs text-theme-secondary mt-1">
                  10% chance of exceeding this value
                </div>
              </div>
              <div className="bg-theme-tertiary rounded-lg p-4">
                <div className="text-sm text-theme-muted mb-1">
                  50th Percentile (Median)
                </div>
                <div className="text-xl font-bold text-theme-primary">
                  {formatCurrency(distribution.percentiles['50'])}
                </div>
                <div className="text-xs text-theme-secondary mt-1">
                  Equal chance of being above or below
                </div>
              </div>
              <div className="bg-theme-tertiary rounded-lg p-4">
                <div className="text-sm text-theme-muted mb-1">
                  10th Percentile
                </div>
                <div className="text-xl font-bold text-theme-primary">
                  {formatCurrency(distribution.percentiles['10'])}
                </div>
                <div className="text-xs text-theme-secondary mt-1">
                  10% chance of falling below this value
                </div>
              </div>
            </div>
          </div>
        </div>}
      </div>
    </div>
  );
}